/**
 * Invoice list view state store.
 *
 * Manages UI state for the invoices list including:
 * - Filter criteria (status, payment_status, supplier, location, date range, search)
 * - Sorting configuration
 * - Active view/tab with preset filters
 * - Custom filter persistence
 *
 * Filter and sort state is persisted to localStorage.
 */

import { create } from 'zustand';
import { persist, createJSONStorage, type PersistStorage } from 'zustand/middleware';

import type { InvoiceGroupBy } from '@/components/invoices/InvoiceGroupHeader';
import { GROUP_OPTIONS } from '@/constants/invoice-options';
import type { InvoiceDocType, InvoiceStatus, PaymentStatus } from '@/types/invoices';

/**
 * Valid groupBy values derived from GROUP_OPTIONS (single source of truth).
 */
const GROUP_BY_VALUES = GROUP_OPTIONS.map((opt) => opt.value) as InvoiceGroupBy[];

/**
 * Storage version for migration handling.
 * Increment when persisted state shape changes in a breaking way.
 *
 * Version history:
 * - 0: Initial (implicit) - status values were lowercase (new, ocr_pending, etc.)
 * - 1: Status values migrated to UPPERCASE (NEW, OCR_PENDING, etc.)
 * - 2: Refresh default views to include newly added tabs
 * - 3: Remove isFilterPanelOpen from persisted state
 * - 4: Redesign tabs: workflow-based order, "К оплате" requires verification
 * - 5: Force re-migration after v4 fix
 * - 6: Add exclude_overdue filter to to_pay view for mutual exclusion with overdue tab
 * - 7: Backfill exclude_overdue in persisted to_pay views/filters
 * - 8: Swap tab order: Просроченные before Оплаченные (action-first logic)
 * - 9: Add "Поставки" tab for DELIVERY_NOTE documents
 * - 10: Restrict "Поставки" tab to verified DELIVERY_NOTE documents
 * - 11: Restrict "К оплате" to payment workflow doc types (exclude delivery notes)
 * - 12: Backfill legacy to_pay payment_status to include not_specified
 * - 13: Backfill legacy paid view to require verification
 * - 14: Payment state is derived from allocations (MD-0092): drop retired
 *       manual statuses (not_specified, cancelled) from persisted filters
 */
const STORAGE_VERSION = 14;

/**
 * Doc types eligible for payment workflow tabs.
 * Delivery docs are handled in "Поставки" and excluded from "К оплате".
 */
export const PAYMENT_WORKFLOW_DOC_TYPES: InvoiceDocType[] = [
  'INVOICE',
  'CREDIT_NOTE',
  'DEBIT_NOTE',
  'PROFORMA',
  'CORRECTION',
  'UNKNOWN',
];

export const TO_PAY_OPEN_PAYMENT_STATUSES: PaymentStatus[] = ['unpaid', 'partial'];

const RETIRED_PAYMENT_STATUSES = new Set(['not_specified', 'cancelled']);
const PAID_PAYMENT_STATUSES: PaymentStatus[] = ['paid'];

type LegacyWorkflowTabFilterNormalizer = (filters: InvoiceFilters) => InvoiceFilters;

function hasSameFilterValues(
  value: PaymentStatus | PaymentStatus[] | undefined,
  expected: PaymentStatus[],
): boolean {
  if (!Array.isArray(value) || value.length !== expected.length) return false;
  const normalized = [...value].sort();
  const normalizedExpected = [...expected].sort();
  return normalized.every((item, index) => item === normalizedExpected[index]);
}

const LEGACY_WORKFLOW_TAB_FILTER_NORMALIZERS: Partial<
  Record<string, LegacyWorkflowTabFilterNormalizer>
> = {
  paid: (filters) =>
    filters.is_verified === undefined
    && hasSameFilterValues(filters.payment_status, PAID_PAYMENT_STATUSES)
      ? {
          ...filters,
          is_verified: true,
        }
      : filters,
};

export function normalizeLegacyWorkflowTabFilters(
  activeView: string | undefined,
  filters: InvoiceFilters,
): InvoiceFilters {
  if (!activeView) {
    return filters;
  }
  return LEGACY_WORKFLOW_TAB_FILTER_NORMALIZERS[activeView]?.(filters) ?? filters;
}

function normalizePersistedWorkflowTabState(
  state: PersistedState,
  targetViewIds: string[],
): void {
  const targetViews = new Set(targetViewIds);

  state.views = state.views.map((view) => {
    if (!targetViews.has(view.id)) {
      return view;
    }

    return {
      ...view,
      filters: normalizeLegacyWorkflowTabFilters(view.id, view.filters),
    };
  });

  state.filters = normalizeLegacyWorkflowTabFilters(state.activeView, state.filters);
}

function dropRetiredPaymentStatuses(filters: InvoiceFilters): InvoiceFilters {
  const value = filters.payment_status as string | string[] | undefined;
  if (value === undefined) return filters;
  const kept = (Array.isArray(value) ? value : [value]).filter(
    (status) => !RETIRED_PAYMENT_STATUSES.has(status),
  ) as PaymentStatus[];
  const { payment_status: _dropped, ...rest } = filters;
  return kept.length > 0 ? { ...rest, payment_status: kept } : rest;
}

/**
 * Migrate status values from lowercase to UPPERCASE.
 * Handles both single values and arrays.
 */
function migrateStatusToUppercase(
  status: string | string[] | undefined
): InvoiceStatus | InvoiceStatus[] | undefined {
  if (!status) return status as undefined;
  const toUpper = (s: string) => s.toUpperCase() as InvoiceStatus;
  return Array.isArray(status) ? status.map(toUpper) : toUpper(status);
}

/**
 * Filter criteria for invoice list.
 * Extended to support all filter options from the design doc.
 */
export interface InvoiceFilters {
  /** Invoice processing status */
  status?: InvoiceStatus | InvoiceStatus[];
  /** Payment status */
  payment_status?: PaymentStatus | PaymentStatus[];
  /** Document type */
  doc_type?: InvoiceDocType | InvoiceDocType[];
  /** Supplier entity ID or comma-separated supplier IDs (OR) */
  supplier_id?: string;
  /** Supplier name for display */
  supplier_name?: string;
  /** Location ID */
  location_id?: string;
  /** Issue date range start (YYYY-MM-DD) */
  issue_date_from?: string;
  /** Issue date range end (YYYY-MM-DD) */
  issue_date_to?: string;
  /** Search query */
  search?: string;
  /** Filter by buyer linkage status (true = known buyer, false = unknown buyer) */
  buyer_is_known?: boolean;
  /** Filter by verification status (true = verified, false = unverified) */
  is_verified?: boolean;
  /** Exclude overdue invoices (used by "К оплате" tab for mutual exclusion with "Просроченные") */
  exclude_overdue?: boolean;
}

/**
 * Input type for setFilters that allows undefined values explicitly.
 * Used to clear filters by setting them to undefined.
 */
export type InvoiceFiltersInput = {
  [K in keyof InvoiceFilters]?: InvoiceFilters[K] | undefined;
};

/**
 * Sort configuration.
 */
export interface SortConfig {
  field: string;
  direction: 'asc' | 'desc';
}

/**
 * View definition with preset filters.
 */
export interface ViewDefinition {
  id: string;
  label: string;
  /** Localized label for UI */
  labelRu: string;
  /** Preset filters applied when this view is active */
  filters: InvoiceFilters;
  /** Default grouping when switching to this view */
  defaultGroupBy: InvoiceGroupBy;
  /** Icon name or identifier (optional) */
  icon?: string;
}

/**
 * Default views from design doc (2026-01-26 redesign).
 *
 * Tab order follows action-first logic: inbox → ready to pay → overdue → paid.
 * "Все" last as archive/search utility.
 *
 * Views:
 * 1. Входящие (Inbox) - is_verified = false (new docs + OCR errors)
 * 2. К оплате (To Pay) - is_verified = true + payable doc_type + payment not completed
 * 3. Просроченные (Overdue) - is_verified = true + payment_status = 'overdue'
 * 4. Оплаченные (Paid) - is_verified = true + payment_status = 'paid'
 * 5. Поставки (Deliveries) - is_verified = true + doc_type = DELIVERY_NOTE
 * 6. Все (All) - no filters, shows unverified indicator
 *
 * Default grouping per view:
 * - inbox: none (process in order)
 * - to_pay: supplier (batch payments)
 * - overdue: supplier (contact supplier)
 * - paid: period (archive browsing)
 * - deliveries: supplier
 * - all: none
 */
export const DEFAULT_VIEWS: ViewDefinition[] = [
  {
    id: 'inbox',
    label: 'Inbox',
    labelRu: 'Входящие',
    filters: {
      is_verified: false,
    },
    defaultGroupBy: 'none',
  },
  {
    id: 'to_pay',
    label: 'To Pay',
    labelRu: 'К оплате',
    filters: {
      is_verified: true,
      doc_type: PAYMENT_WORKFLOW_DOC_TYPES,
      payment_status: TO_PAY_OPEN_PAYMENT_STATUSES,
      // Note: backend also includes documents without a known total (NULL payment_status).
      // exclude_overdue ensures mutual exclusion with "Просроченные" tab
      exclude_overdue: true,
    },
    defaultGroupBy: 'supplier',
  },
  {
    id: 'overdue',
    label: 'Overdue',
    labelRu: 'Просроченные',
    filters: {
      is_verified: true,
      payment_status: ['overdue'],
    },
    defaultGroupBy: 'supplier',
  },
  {
    id: 'paid',
    label: 'Paid',
    labelRu: 'Оплаченные',
    filters: {
      is_verified: true,
      payment_status: ['paid'],
    },
    defaultGroupBy: 'period',
  },
  {
    id: 'deliveries',
    label: 'Deliveries',
    labelRu: 'Поставки',
    filters: {
      is_verified: true,
      doc_type: ['DELIVERY_NOTE'],
    },
    defaultGroupBy: 'supplier',
  },
  {
    id: 'all',
    label: 'All',
    labelRu: 'Все',
    filters: {},
    defaultGroupBy: 'none',
  },
];

const DEFAULT_VIEW_IDS = new Set(DEFAULT_VIEWS.map((view) => view.id));

function resetViewsToDefaults(
  views: ViewDefinition[],
  options?: { extraExcludedIds?: Iterable<string> },
): ViewDefinition[] {
  const extraExcludedIds = new Set(options?.extraExcludedIds ?? []);
  const customViews = views.filter(
    (view) => !DEFAULT_VIEW_IDS.has(view.id) && !extraExcludedIds.has(view.id),
  );
  return [...DEFAULT_VIEWS, ...customViews];
}

/**
 * Invoice view state and actions.
 */
export interface InvoicesViewState {
  /**
   * Current filter criteria.
   */
  filters: InvoiceFilters;

  /**
   * Current sort configuration.
   */
  sort: SortConfig;

  /**
   * Currently active view/tab identifier.
   */
  activeView: string;

  /**
   * Current grouping mode.
   */
  groupBy: InvoiceGroupBy;

  /**
   * Available views (default + custom).
   */
  views: ViewDefinition[];

  /**
   * Merge new filter values with existing filters.
   * Undefined values will clear that filter.
   */
  setFilters: (filters: InvoiceFiltersInput) => void;

  /**
   * Reset all filters and switch to "All" view with no grouping.
   * Use when user wants to see all documents without any filtering.
   */
  resetFilters: () => void;

  /**
   * Set sort field and direction.
   * If direction is not provided, defaults to 'asc'.
   */
  setSort: (field: string, direction?: 'asc' | 'desc') => void;

  /**
   * Toggle sort direction for a field.
   * If sorting by different field, sets to asc.
   * If sorting by same field, toggles direction.
   */
  toggleSort: (field: string) => void;

  /**
   * Set the active view/tab and apply its preset filters.
   */
  setActiveView: (viewId: string) => void;

  /**
   * Set grouping mode.
   */
  setGroupBy: (groupBy: InvoiceGroupBy) => void;

  /**
   * Get the filters to send to API.
   * Combines active view filters with manual filters.
   */
  getApiFilters: () => InvoiceFilters;
}

/**
 * Default filter state.
 */
const DEFAULT_FILTERS: InvoiceFilters = {};

/**
 * Default sort configuration.
 */
const DEFAULT_SORT: SortConfig = {
  field: 'created_at',
  direction: 'desc',
};

/**
 * Default active view.
 * Inbox is the primary landing page — shows what needs attention.
 */
const DEFAULT_ACTIVE_VIEW = 'inbox';

/**
 * localStorage key for persisting view state.
 */
const STORAGE_KEY = 'invoices-view';

/**
 * Persisted state shape.
 */
interface PersistedState {
  filters: InvoiceFilters;
  sort: SortConfig;
  activeView: string;
  groupBy: InvoiceGroupBy;
  views: ViewDefinition[];
}

function ensureToPayExcludeOverdueInPersistedState(state: PersistedState): void {
  state.views = state.views.map((view) => {
    if (view.id !== 'to_pay') return view;
    return {
      ...view,
      filters: {
        ...view.filters,
        exclude_overdue: true,
      },
    };
  });

  if (state.activeView === 'to_pay') {
    state.filters = {
      ...state.filters,
      exclude_overdue: true,
    };
  }
}

/**
 * Get the view definition by ID.
 */
function getViewById(views: ViewDefinition[], viewId: string): ViewDefinition | undefined {
  return views.find((v) => v.id === viewId);
}

function areFilterValuesEqual(
  left: InvoiceFilters[keyof InvoiceFilters] | undefined,
  right: InvoiceFilters[keyof InvoiceFilters] | undefined,
): boolean {
  if (Array.isArray(left) || Array.isArray(right)) {
    if (!Array.isArray(left) || !Array.isArray(right)) return false;
    if (left.length !== right.length) return false;
    return left.every((value, index) => value === right[index]);
  }
  return left === right;
}

function dropCurrentViewPresetFilters(
  filters: InvoiceFilters,
  currentView: ViewDefinition | undefined,
): InvoiceFilters {
  if (!currentView) return { ...filters };

  const nextFilters: InvoiceFilters = { ...filters };
  for (const [rawKey, presetValue] of Object.entries(currentView.filters)) {
    const key = rawKey as keyof InvoiceFilters;
    if (areFilterValuesEqual(nextFilters[key], presetValue as InvoiceFilters[keyof InvoiceFilters])) {
      delete nextFilters[key];
    }
  }

  return nextFilters;
}

/**
 * Invoice view state store with persistence.
 *
 * Uses Zustand persist middleware to save filters, sort, and activeView
 * to localStorage.
 */
export const useInvoicesViewStore = create<InvoicesViewState>()(
  persist(
    (set, get) => ({
      // State
      filters: DEFAULT_FILTERS,
      sort: DEFAULT_SORT,
      activeView: DEFAULT_ACTIVE_VIEW,
      groupBy: 'none' as InvoiceGroupBy,
      views: DEFAULT_VIEWS,

      // Actions
      setFilters: (newFilters) => {
        set((state) => {
          // Clean up undefined values
          const cleanFilters = { ...state.filters };
          for (const [key, value] of Object.entries(newFilters)) {
            if (value === undefined || value === '' || (Array.isArray(value) && value.length === 0)) {
              delete cleanFilters[key as keyof InvoiceFilters];
            } else {
              (cleanFilters as Record<string, unknown>)[key] = value;
            }
          }
          return { filters: cleanFilters };
        });
      },

      resetFilters: () => {
        // Reset filters = show all documents = switch to "All" tab
        // Also reset groupBy to match "All" view's default (none)
        set({
          filters: DEFAULT_FILTERS,
          activeView: 'all',
          groupBy: 'none',
        });
      },

      setSort: (field, direction = 'asc') => {
        set({ sort: { field, direction } });
      },

      toggleSort: (field) => {
        const currentSort = get().sort;
        if (currentSort.field === field) {
          // Toggle direction
          set({
            sort: {
              field,
              direction: currentSort.direction === 'asc' ? 'desc' : 'asc',
            },
          });
        } else {
          // New field, default to asc
          set({ sort: { field, direction: 'asc' } });
        }
      },

      setActiveView: (viewId) => {
        const state = get();
        const view = getViewById(state.views, viewId);
        const currentView = getViewById(state.views, state.activeView);
        const filtersWithoutCurrentPreset = dropCurrentViewPresetFilters(state.filters, currentView);

        if (view) {
          // Apply view's preset filters but preserve current grouping
          // Only use defaultGroupBy if groupBy is currently undefined/invalid
          const currentGroupByValid = GROUP_BY_VALUES.includes(state.groupBy);
          set({
            activeView: viewId,
            filters: normalizeLegacyWorkflowTabFilters(
              viewId,
              { ...filtersWithoutCurrentPreset, ...view.filters },
            ),
            groupBy: currentGroupByValid ? state.groupBy : view.defaultGroupBy,
          });
        } else {
          // Fallback to 'all' view, preserve grouping if valid
          const currentGroupByValid = GROUP_BY_VALUES.includes(state.groupBy);
          const allView = getViewById(state.views, 'all');
          set({
            activeView: 'all',
            filters: { ...filtersWithoutCurrentPreset, ...(allView?.filters ?? DEFAULT_FILTERS) },
            groupBy: currentGroupByValid ? state.groupBy : 'none',
          });
        }
      },

      setGroupBy: (groupBy) => {
        set({ groupBy });
      },

      getApiFilters: () => {
        const state = get();
        const view = getViewById(state.views, state.activeView);
        const viewFilters = view?.filters ?? {};

        // Merge view filters with manual filters
        // Manual filters override view filters
        return {
          ...viewFilters,
          ...state.filters,
        };
      },

    }),
    {
      name: STORAGE_KEY,
      version: STORAGE_VERSION,
      storage: createJSONStorage(() => localStorage) as PersistStorage<PersistedState>,
      migrate: (persistedState: unknown, version: number): PersistedState => {
        const state = persistedState as PersistedState & { isFilterPanelOpen?: boolean };

        if (version < 1) {
          // Migrate lowercase status values to UPPERCASE
          if (state.filters.status) {
            const migratedStatus = migrateStatusToUppercase(state.filters.status);
            if (migratedStatus) {
              state.filters.status = migratedStatus;
            }
          }
          // Also migrate view preset filters
          state.views = state.views.map((view) => {
            if (!view.filters.status) {
              return view;
            }
            const migratedStatus = migrateStatusToUppercase(view.filters.status);
            if (!migratedStatus) {
              return view;
            }
            return {
              ...view,
              filters: {
                ...view.filters,
                status: migratedStatus,
              },
            };
          });
        }

        if (version < 2) {
          const existingViews = Array.isArray(state.views) ? state.views : [];
          const existingById = new Map(existingViews.map((view) => [view.id, view]));
          // Merge defaultGroupBy from DEFAULT_VIEWS into existing views
          const mergedDefaults = DEFAULT_VIEWS.map((defaultView) => {
            const existing = existingById.get(defaultView.id);
            if (!existing) return defaultView;
            return {
              ...existing,
              defaultGroupBy: existing.defaultGroupBy,
            };
          });
          const customViews = existingViews.filter(
            (view) => !DEFAULT_VIEWS.some((defaultView) => defaultView.id === view.id),
          );

          state.views = [...mergedDefaults, ...customViews];

          if (!state.views.find((view) => view.id === state.activeView)) {
            state.activeView = DEFAULT_ACTIVE_VIEW;
          }
        }

        if (version < 3) {
          delete state.isFilterPanelOpen;
        }

        if (version < 5) {
          // Redesign tabs: workflow-based order, new tab IDs
          // Reset to new DEFAULT_VIEWS, preserving only custom views
          const oldDefaultIds = new Set(['all', 'unverified', 'verified', 'to_pay', 'paid', 'overdue', 'to_review']);
          state.views = resetViewsToDefaults(state.views, {
            extraExcludedIds: oldDefaultIds,
          });

          // Reset activeView if it was an old default that no longer exists
          if (!state.views.find((view) => view.id === state.activeView)) {
            state.activeView = DEFAULT_ACTIVE_VIEW;
          }
          // Also reset filters to match new active view
          const activeView = state.views.find((v) => v.id === state.activeView);
          if (activeView) {
            state.filters = { ...activeView.filters };
          }
        }

        if (version < 6) {
          // Add exclude_overdue to to_pay view for mutual exclusion with overdue tab
          ensureToPayExcludeOverdueInPersistedState(state);
        }

        if (version < 7) {
          // Ensure exclude_overdue is enforced for to_pay after prior persistence
          ensureToPayExcludeOverdueInPersistedState(state);
        }

        if (version < 8) {
          // Swap tab order: Просроченные before Оплаченные (action-first logic)
          // Reset views to new DEFAULT_VIEWS order, preserving custom views
          state.views = resetViewsToDefaults(state.views);
        }

        if (version < 9) {
          // Add "Поставки" tab and ensure persisted default tab order matches DEFAULT_VIEWS.
          state.views = resetViewsToDefaults(state.views);
        }

        if (version < 10) {
          // Restrict "Поставки" to verified DELIVERY_NOTE in persisted defaults.
          state.views = resetViewsToDefaults(state.views);

          // Keep current tab behavior aligned after migration.
          if (state.activeView === 'deliveries' && state.filters.is_verified === undefined) {
            state.filters = {
              ...state.filters,
              is_verified: true,
            };
          }
        }

        if (version < 11) {
          // Restrict "К оплате" to non-delivery payment workflow document types.
          state.views = state.views.map((view) => {
            if (view.id !== 'to_pay') return view;
            return {
              ...view,
              filters: {
                ...view.filters,
                doc_type: PAYMENT_WORKFLOW_DOC_TYPES,
              },
            };
          });
          if (state.activeView === 'to_pay' && state.filters.doc_type === undefined) {
            state.filters = {
              ...state.filters,
              doc_type: PAYMENT_WORKFLOW_DOC_TYPES,
            };
          }
        }

        if (version < 12) {
          // Old persisted to_pay state used ['unpaid', 'partial'], which hides
          // 'Не указано' invoices and desynchronizes the list from tab counts.
          normalizePersistedWorkflowTabState(state, ['to_pay']);
        }

        if (version < 13) {
          normalizePersistedWorkflowTabState(state, ['paid']);
        }

        if (version < 14) {
          state.views = state.views.map((view) => ({
            ...view,
            filters: dropRetiredPaymentStatuses(view.filters),
          }));
          state.filters = dropRetiredPaymentStatuses(state.filters);
        }

        return state;
      },
      // Only persist filters, sort, activeView, groupBy, and views
      partialize: (state): PersistedState => ({
        filters: state.filters,
        sort: state.sort,
        activeView: state.activeView,
        groupBy: state.groupBy,
        views: state.views,
      }),
    },
  ),
);

/**
 * Selector for checking if any manual filters are active.
 * (Excludes view preset filters)
 */
export const selectHasActiveFilters = (state: InvoicesViewState): boolean => {
  const { filters } = state;
  return (
    filters.status !== undefined ||
    filters.payment_status !== undefined ||
    filters.doc_type !== undefined ||
    filters.supplier_id !== undefined ||
    filters.location_id !== undefined ||
    filters.issue_date_from !== undefined ||
    filters.issue_date_to !== undefined ||
    filters.buyer_is_known !== undefined ||
    filters.is_verified !== undefined ||
    (filters.search !== undefined && filters.search.length > 0)
  );
};

/**
 * Selector for getting the current view definition.
 */
export const selectCurrentView = (state: InvoicesViewState): ViewDefinition | undefined => {
  return state.views.find((v) => v.id === state.activeView);
};

/**
 * Selector for getting active filter count (for badge display).
 */
export const selectActiveFilterCount = (state: InvoicesViewState): number => {
  const { filters } = state;
  let count = 0;

  if (filters.status !== undefined) count++;
  if (filters.payment_status !== undefined) count++;
  if (filters.doc_type !== undefined) count++;
  if (filters.supplier_id !== undefined) count++;
  if (filters.location_id !== undefined) count++;
  if (filters.issue_date_from !== undefined || filters.issue_date_to !== undefined) count++;
  if (filters.buyer_is_known !== undefined) count++;
  if (filters.is_verified !== undefined) count++;
  if (filters.search !== undefined && filters.search.length > 0) count++;

  return count;
};
